import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { images } from '../../data/content';
import { ArrowDown, CalendarCheck } from 'lucide-react';

const HeroSection = ({ onRegisterClick }) => {
  const { t, isRTL } = useLanguage();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center bg-charcoal overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={images.hero.main}
          alt="Healing sanctuary"
          className="w-full h-full object-cover object-top opacity-50"
        />
        {/* Layered overlay: warm amber tint underneath, dark charcoal on top for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/75 via-charcoal/45 to-charcoal/92" />
        {/* Warm amber wash — adds healing warmth without overriding text contrast */}
        <div className="absolute inset-0 bg-gradient-to-tr from-amber-950/30 via-transparent to-transparent" />
        {/* Central gold glow — larger and slightly more visible on all screen sizes */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-gold/8 rounded-full blur-3xl" />
        {/* Upper-center soft warm halo to lift the image atmosphere */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-amber-900/10 rounded-full blur-3xl hidden sm:block" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-10 relative z-10 pt-28 sm:pt-32 lg:pt-52 pb-16 sm:pb-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Main Headline - with yellow accent on key words */}
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight mb-6 sm:mb-8 animate-fade-in-up px-2">
            {isRTL ? (
              <>
                <span className="text-cream">مساحة هادئة وموثوقة للنساء لـ</span>
                <span className="text-gold">لشفاء</span>
                <span className="text-cream"> و</span>
                <span className="text-gold">النمو</span>
                <span className="text-cream"> و</span>
                <span className="text-gold">إعادة الاتصال</span>
                <span className="text-cream"> بأنفسهن</span>
              </>
            ) : (
              <>
                <span className="text-cream">A calm, trusted space for women to </span>
                <span className="text-gold">heal</span>
                <span className="text-cream">, </span>
                <span className="text-gold">grow</span>
                <span className="text-cream">, and </span>
                <span className="text-gold">reconnect</span>
                <span className="text-cream"> with themselves</span>
              </>
            )}
          </h1>

          {/* Subtext */}
          <p className="text-cream-300 text-base sm:text-lg lg:text-xl leading-relaxed mb-8 sm:mb-12 max-w-2xl mx-auto animate-fade-in-up px-4" style={{ animationDelay: '0.2s' }}>
            {t.hero.subtext}
          </p>

          {/* CTA Buttons - Stack on mobile */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 animate-fade-in-up px-4 sm:px-0" style={{ animationDelay: '0.4s' }}>
            <button
              onClick={onRegisterClick}
              className="btn-gold glow-yellow flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              <CalendarCheck size={18} />
              <span>{isRTL ? 'احجزي مكالمة اكتشافية مجانية' : 'Book a Free Discovery Call'}</span>
            </button>
            <button
              onClick={() => scrollToSection('programs')}
              className="btn-outline-gold w-full sm:w-auto flex items-center gap-1 justify-center"
            >
              {t.hero.cta}
              <span aria-hidden="true">{isRTL ? ' ←' : ' →'}</span>
            </button>
          </div>
        </div>

        {/* Scroll Indicator - Hide on very small screens */}
        <div className="absolute bottom-6 sm:bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce hidden sm:block">
          <button
            onClick={() => scrollToSection('mission')}
            className="text-gold hover:text-gold/80 transition-colors p-2"
            aria-label="Scroll down"
          >
            <ArrowDown size={28} className="drop-shadow-[0_0_8px_rgba(243,210,34,0.5)]" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;